import { Collection }                     from '@mathquis/modelx';
import { makeAutoObservable, observable } from 'mobx';
import { ApiCollection }                  from 'collections/ApiCollection';
import { PagedCollection }                from 'collections/PagedCollection';
import { MESSAGES_PER_PAGE }              from 'components/Stand/Dashboard/StandDashboardStandMessageListBox';
import { ROLE_STANDIST_DECORATOR }        from 'constants/StandistRoleReference';
import { ROLE_STANDIST_AGENCY }           from 'constants/StandistRoleReference';
import { ROLE_STANDIST_EXHIBITOR }        from 'constants/StandistRoleReference';
import ShowModel                          from 'models/ShowModel';
import DemandModel                        from 'models/DemandModel';
import DemandStatusModel                  from 'models/DemandStatusModel';
import DemandTypeModel                    from 'models/DemandTypeModel';
import LayoutTypeModel                    from 'models/LayoutTypeModel';
import StandLayoutModel                   from 'models/StandLayoutModel';
import StandMediaModel                    from 'models/StandMediaModel';
import StandMessageModel                  from 'models/StandMessageModel';
import UserStandRoleModel                 from 'models/UserStandRoleModel';
import DemandConfigModel                  from 'models/DemandConfigModel';
import MessageModel                       from 'models/MessageModel';
import ReportModel                        from 'models/ReportModel';
import UserModel                          from 'models/UserModel';
import StandModel                         from 'models/StandModel';
import MessageMediaModel                  from 'models/MessageMediaModel';
import MountingAttestationModel           from 'models/MountingAttestationModel';
import currentSession                     from 'utils/currentSession';

export default class StandDashboardStore {
  @observable public collDemandConfig = new ApiCollection(DemandConfigModel);
  @observable public collDemandStatus = new ApiCollection(DemandStatusModel);
  @observable public collDemandType = new ApiCollection(DemandTypeModel);
  @observable public collDocument = new ApiCollection(StandMediaModel);
  @observable public collLayoutType = new ApiCollection(LayoutTypeModel);
  @observable public collMessage = new ApiCollection(MessageModel);
  @observable public collMessageMedia = new ApiCollection(MessageMediaModel);
  @observable public collMountingAttestation = new ApiCollection(MountingAttestationModel);
  @observable public collPagedDemand = new PagedCollection(DemandModel);
  @observable public collPagedStandLayout = new PagedCollection(StandLayoutModel);
  @observable public collPagedStandMessage = new PagedCollection(StandMessageModel);
  @observable public collReport = new ApiCollection(ReportModel);
  @observable public collStandist = new ApiCollection(UserModel);
  @observable public collUserStandRole = new ApiCollection(UserStandRoleModel);

  @observable public show = new ShowModel();
  @observable public stand = new StandModel();

  @observable public virtualCollAgency: Collection<UserModel>;
  @observable public virtualCollDecorator: Collection<UserModel>;
  @observable public virtualCollExhibitor: Collection<UserModel>;

  constructor() {
    makeAutoObservable(this);

    this.collPagedStandMessage.itemsPerPage = MESSAGES_PER_PAGE;

    this.virtualCollExhibitor = this.collStandist.createVirtualCollection(
      (user: UserModel) =>
        !!this.collUserStandRole.find(
          (userStandRole: UserStandRoleModel) =>
            userStandRole.userIri === user.iri &&
            userStandRole.standistRole === ROLE_STANDIST_EXHIBITOR,
        ),
    );

    this.virtualCollAgency = this.collStandist.createVirtualCollection(
      (user: UserModel) =>
        !!this.collUserStandRole.find(
          (userStandRole: UserStandRoleModel) =>
            userStandRole.userIri === user.iri &&
            userStandRole.standistRole === ROLE_STANDIST_AGENCY,
        ),
    );

    this.virtualCollDecorator = this.collStandist.createVirtualCollection(
      (user: UserModel) =>
        !!this.collUserStandRole.find(
          (userStandRole: UserStandRoleModel) =>
            userStandRole.userIri === user.iri &&
            userStandRole.standistRole === ROLE_STANDIST_DECORATOR,
        ),
    );
  }

  public fetchData = async (standId: id) => {
    try {
      await this.stand.set('id', standId).fetch();
    } catch (error: any) {
      const requestStatus = error?.connectorError?.request?.status || error?.connectorError?.response?.status;

      if (requestStatus === 404) {
        const error = new Error('stand_not_found');
        error.name = 'stand_not_found';
        throw error;
      }

      if (requestStatus === 405) {
        const error = new Error('get_stand_unauthorized');
        error.name = 'get_stand_unauthorized';
        throw error;
      }

      return;
    }

    const promises: Promise<any>[] = [
      this.show
        .set({ id: this.stand.showId })
        .fetch(),

      this.collDemandConfig
        .setFilter('show', this.stand.showId)
        .list(),

      this.collPagedDemand
        .setFilter('stand', standId)
        .setSorts({
          createdAt: false,
          id: false,
        })
        .list(),

      this.fetchMessages(standId),

      this.fetchRoleCollectionAndStandistsData(standId),

      this.collReport
        .setFilter('stand', this.stand.id)
        .setSort('createdAt', false)
        .list(),

      this.collDocument.setFilter('stand', standId).list(),

      this.collPagedStandLayout
        .setFilter('stand', this.stand.id)
        .list(),
    ];

    if (currentSession.isAdminOrSupport) {
      promises.push(
        this.collMountingAttestation.setFilter('stand', standId).list(),
      );
    }

    await Promise.all(promises);
  };

  public fetchMessages = async (standId: id) => {
    await this.collPagedStandMessage
      .setFilter('stand', standId)
      .setSorts({
        'message.createdAt': false,
        'message.id': false,
      })
      .list()
      .then(async () => {
        const messagesIds = this.collPagedStandMessage.getUniques('message.id');

        if (messagesIds.length) {
          await Promise.all([
            this.collMessage.setFilter('id', messagesIds).list(),

            this.collMessageMedia.setFilter('message', messagesIds).list(),
          ]);
        }
      });
  };

  public fetchRoleCollectionAndStandistsData = async (standId: id) => {
    await this.collUserStandRole.setFilter('stand', standId).list();

    const usersIris = this.collUserStandRole.getUniques('user');
    await this.collStandist.setFilter('id', usersIris).list();
  };

  public fetchStatusesAndTypes = async () => {
    await Promise.all([
      this.collDemandStatus.list(),
      this.collDemandType.list(),
      this.collLayoutType.list(),
    ]);
  };
}
